import { useMutation } from "@apollo/react-hooks";
import { useAsyncCallback } from 'react-use-async-callback';
import gql from "graphql-tag";
import { AsyncActionStatus } from '../../../shared/abstractStore';
import { QuestionAnswerUpdateInput } from '../generated/globalTypes';
import { updateQuestionAnswerMutation, updateQuestionAnswerMutationVariables } from '../generated/updateQuestionAnswerMutation';
import { questionAnswerFieldsFragment } from '../models/QuestionAnswer';

/**
 * Get a callback to update a QuestionAnswer in the store.
 */
export function useUpdateQuestionAnswerCallback(): [(id: string, model: QuestionAnswerUpdateInput) => Promise<void>, AsyncActionStatus] {
    const [mutationAction, { error }] = useMutation<updateQuestionAnswerMutation, updateQuestionAnswerMutationVariables>(
        gql`
            mutation updateQuestionAnswerMutation ($model: QuestionAnswerUpdateInput!) {
                updateQuestionAnswer(model: $model) {
                    ...questionAnswerFields
                }
            }

            ${questionAnswerFieldsFragment}
        `
    );

    // Wrap the mutation in a standard callback.
    const [action, { isExecuting, errors: actionErrors }] = useAsyncCallback(async (id: string, model: QuestionAnswerUpdateInput) => {
        const result = await mutationAction({ variables: { model: { ...model, id: id } } });
        if (result.errors && result.errors.length) {
            throw result.errors;
        }
    }, [mutationAction]);

    return [action, { isExecuting: isExecuting, errors: error ? error.message : actionErrors }];
}
